import React, { useEffect, useRef, useState } from 'react';
import ExperimentNav from '../../components/ExperimentNav';
import { experiments } from '../../App';
import './AsciiLens.css';

// Brightness ramp — darkest to lightest. Char chosen by normalized luminance.
const CHARS = ' .:;+*#@';

// Poem — drawn into the source canvas so its pixels can be sampled.
const POEM_LINES = [
  'LOOK CLOSE',
  'AND THE WORDS DISSOLVE.',
  'A LENS OF SYMBOLS',
  'WHERE LANGUAGE WAS.',
  '',
  'PUSH THE CURSOR FASTER',
  'AND THE GRID CONTRACTS.',
  'PAUSE — THE TEXT REASSEMBLES',
  'INTO ITSELF.',
  '',
  'CLICK TO FREEZE THE GAZE.',
  'THIS PAGE IS A PHASE STATE',
  'BETWEEN SEEN AND READ.',
];

const LENS_RADIUS = 200;
const CELL_MAX = 8;
const CELL_MIN = 4;
const SPEED_MAX = 2.0; // px/ms — speed at which cell hits CELL_MIN
const EMA_ALPHA = 0.2;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

const AsciiLens: React.FC = () => {
  const sourceRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);

  // Cursor & motion
  const cursorRef = useRef({ x: 0, y: 0 });
  const lastMouseRef = useRef({ x: 0, y: 0, t: 0 });
  const smoothSpeedRef = useRef(0);

  // Freeze toggle (animated 0..1)
  const freezeTargetRef = useRef(0);
  const freezeAmountRef = useRef(0);

  // Source pixel cache — full-frame image data, re-read on resize
  const sourceImgRef = useRef<ImageData | null>(null);
  const sizeRef = useRef({ w: 0, h: 0 });

  const [hudCell, setHudCell] = useState(8);
  const [hudSpeed, setHudSpeed] = useState(0);
  const [hudFrozen, setHudFrozen] = useState(false);
  const hudFrameRef = useRef(0);

  const exp = experiments.find(e => e.id === 'ascii-lens');

  useEffect(() => {
    const source = sourceRef.current;
    const overlay = overlayRef.current;
    if (!source || !overlay) return;

    const sCtx = source.getContext('2d');
    const oCtx = overlay.getContext('2d');
    if (!sCtx || !oCtx) return;

    // ── Render the source canvas (gradient + poem) ─────────
    const drawSource = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      source.width = w;
      source.height = h;
      overlay.width = w;
      overlay.height = h;
      sizeRef.current = { w, h };

      // Soft radial gradient (light grayscale)
      const g = sCtx.createRadialGradient(w * 0.5, h * 0.5, 0, w * 0.5, h * 0.5, Math.hypot(w, h) * 0.6);
      g.addColorStop(0, '#f4f4f4');
      g.addColorStop(0.55, '#dcdcdc');
      g.addColorStop(1, '#bcbcbc');
      sCtx.fillStyle = g;
      sCtx.fillRect(0, 0, w, h);

      // Poem — large dark serif-ish (just monospace heavy here)
      const fontSize = clamp(Math.min(w, h) * 0.052, 28, 68);
      sCtx.fillStyle = '#1a1a1a';
      sCtx.textBaseline = 'middle';
      sCtx.textAlign = 'center';
      sCtx.font = `700 ${fontSize}px 'JetBrains Mono', 'VT323', monospace`;
      const lineHeight = fontSize * 1.35;
      const totalH = lineHeight * POEM_LINES.length;
      const startY = h / 2 - totalH / 2 + lineHeight / 2;
      for (let i = 0; i < POEM_LINES.length; i++) {
        sCtx.fillText(POEM_LINES[i], w / 2, startY + i * lineHeight);
      }

      // Cache image data for fast sampling
      sourceImgRef.current = sCtx.getImageData(0, 0, w, h);
    };

    drawSource();

    // ── Initial cursor at center ─────────────────────────
    cursorRef.current = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    lastMouseRef.current = { x: cursorRef.current.x, y: cursorRef.current.y, t: performance.now() };

    // ── Listeners ────────────────────────────────────────
    const onMove = (e: PointerEvent) => {
      const now = performance.now();
      const dx = e.clientX - lastMouseRef.current.x;
      const dy = e.clientY - lastMouseRef.current.y;
      const dt = Math.max(1, now - lastMouseRef.current.t);
      const speed = Math.sqrt(dx * dx + dy * dy) / dt; // px / ms
      smoothSpeedRef.current = EMA_ALPHA * speed + (1 - EMA_ALPHA) * smoothSpeedRef.current;
      cursorRef.current = { x: e.clientX, y: e.clientY };
      lastMouseRef.current = { x: e.clientX, y: e.clientY, t: now };
    };

    const onClick = () => {
      freezeTargetRef.current = freezeTargetRef.current > 0.5 ? 0 : 1;
    };

    const onResize = () => {
      drawSource();
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerdown', onMove);
    window.addEventListener('click', onClick);
    window.addEventListener('resize', onResize);

    // ── Animation loop ───────────────────────────────────
    const render = () => {
      const { w, h } = sizeRef.current;
      const img = sourceImgRef.current;
      if (!img) {
        rafRef.current = requestAnimationFrame(render);
        return;
      }

      // Animate freeze
      freezeAmountRef.current += (freezeTargetRef.current - freezeAmountRef.current) * 0.08;
      const fz = freezeAmountRef.current;

      // Effective lens radius: 200px → > screen diagonal as freeze→1
      // Uses 1.2× hypot so the circle covers every cell even if cursor
      // sits at a screen corner.
      const maxR = Math.hypot(w, h) * 1.2;
      const r = lerp(LENS_RADIUS, maxR, fz);
      const r2 = r * r;

      // Cell size from cursor speed (EMA)
      const normSpeed = clamp(smoothSpeedRef.current / SPEED_MAX, 0, 1);
      const cell = Math.max(CELL_MIN, Math.round(CELL_MAX - normSpeed * (CELL_MAX - CELL_MIN)));

      // Clear overlay
      oCtx.clearRect(0, 0, w, h);

      // Fill the lens area with the page background color so the underlying
      // source canvas no longer shows through inside the lens.
      oCtx.save();
      oCtx.beginPath();
      oCtx.arc(cursorRef.current.x, cursorRef.current.y, r, 0, Math.PI * 2);
      oCtx.clip();

      // Background fill inside lens (slightly lighter than source so contrast)
      oCtx.fillStyle = '#ededed';
      oCtx.fillRect(0, 0, w, h);

      // Sample bounding box for cells inside lens (clamped to canvas)
      const cx = cursorRef.current.x;
      const cy = cursorRef.current.y;
      const x0 = Math.max(0, Math.floor((cx - r) / cell) * cell);
      const y0 = Math.max(0, Math.floor((cy - r) / cell) * cell);
      const x1 = Math.min(w, Math.ceil((cx + r) / cell) * cell);
      const y1 = Math.min(h, Math.ceil((cy + r) / cell) * cell);

      // Draw chars
      oCtx.fillStyle = '#1a1a1a';
      oCtx.textBaseline = 'top';
      oCtx.textAlign = 'left';
      oCtx.font = `${cell}px 'JetBrains Mono', monospace`;

      const data = img.data;
      const stride = w * 4;

      for (let py = y0; py < y1; py += cell) {
        const sampleY = py + (cell >> 1);
        for (let px = x0; px < x1; px += cell) {
          const sampleX = px + (cell >> 1);
          // circle test using cell centers
          const ddx = sampleX - cx;
          const ddy = sampleY - cy;
          if (ddx * ddx + ddy * ddy > r2) continue;

          if (sampleX < 0 || sampleY < 0 || sampleX >= w || sampleY >= h) continue;
          const i = sampleY * stride + sampleX * 4;
          // Grayscale luminance (Rec.601) on source pixel
          const lum = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114) / 255;
          // INVERT: dark source → dense char (@), light source → space.
          // The source has dark text on a light gradient, so we want chars to
          // appear where the original ink was.
          const t = 1 - lum;
          const idx = Math.floor(t * (CHARS.length - 1) + 0.0001);
          const ch = CHARS[idx];
          if (ch === ' ') continue;
          oCtx.fillText(ch, px, py);
        }
      }

      oCtx.restore();

      // HUD throttle (~10fps)
      hudFrameRef.current++;
      if (hudFrameRef.current % 6 === 0) {
        setHudCell(cell);
        setHudSpeed(Math.round(smoothSpeedRef.current * 1000)); // px/sec
        setHudFrozen(fz > 0.5);
      }

      rafRef.current = requestAnimationFrame(render);
    };

    rafRef.current = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onMove);
      window.removeEventListener('click', onClick);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <div className="asciiLens">
      <ExperimentNav prompt={exp?.prompt} title={exp?.title} />

      <canvas ref={sourceRef} className="layer source" />
      <canvas ref={overlayRef} className="layer overlay" />

      <div className="asciiHint">▸ move · click to freeze</div>

      <div className="asciiHud">
        <span>cell <span className="hudValue">{hudCell}px</span></span>
        <span>speed <span className="hudValue">{hudSpeed}</span> px/s</span>
        <span>{hudFrozen ? 'frozen' : 'lens'}</span>
      </div>
    </div>
  );
};

export default AsciiLens;
